import { AlkLogger } from '@alk/core';
import { bufferTime, filter, finalize, map, takeUntil } from 'rxjs/operators';
import { compact, THROTLE_NOTIFICATIONS_RESOLVE } from '../../../helpers';
import { inject, injectable } from 'inversify';
import { INotificationMutationResolveService } from './notification-mutation-resolve.interface';
import { INotificationMutationService, NotificationMutationObservable } from '../../../interfaces';
import {
    INotificationsResolveAdapterService,
    NOTIFICATIONS_RESOLVE_ADAPTER_SERVICE,
} from '../../message-adapters/notifications-resolve-adapter';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../../session-lifecycle';
import { mapOver } from '../../../helpers/array-utils';
import { resolveMutation } from './notification-mutation-resolve.service.utils';
import { Subject } from 'rxjs';

@injectable()
export class NotificationMutationResolveService implements INotificationMutationService, INotificationMutationResolveService {

    @inject(NOTIFICATIONS_RESOLVE_ADAPTER_SERVICE) private readonly notificationsResolveAdapter: INotificationsResolveAdapterService;
    @inject(SESSION_LIFECYCLE_SERVICE) private readonly sessionLifeCycle: ISessionLifecycle;

    private resolveRequestObs$: Subject<string>;
    private mutationsObs$: NotificationMutationObservable;

    private readonly getResolveObs = (): Subject<string> => {

        if (Boolean(this.resolveRequestObs$)) {
            return this.resolveRequestObs$;
        }

        AlkLogger.info('[NotificationMutationResolveService] inited');

        this.resolveRequestObs$ = new Subject<string>();

        this.resolveRequestObs$.pipe(
            takeUntil(this.sessionLifeCycle.destroyObservable()),
            bufferTime(THROTLE_NOTIFICATIONS_RESOLVE),
            finalize(() => this.clean()),
            compact(),
            filter(notifications => Boolean(notifications?.length)),
        ).subscribe(this.notificationsResolveAdapter.resolveNotifications);

        return this.resolveRequestObs$;
    };

    private readonly clean = () => {
        AlkLogger.info('[NotificationMutationResolveService#clean]');
        delete this.mutationsObs$;
        delete this.resolveRequestObs$;
    };

    resolve = (id: string): void => this.getResolveObs().next(id);

    mutations = (): NotificationMutationObservable =>
        this.mutationsObs$ ?? (this.mutationsObs$ =
            this.notificationsResolveAdapter.notificationsObs().pipe(
                takeUntil(this.sessionLifeCycle.destroyObservable()),
                map(mapOver(resolveMutation)),
            ));
}
